import { DndContext, useDroppable } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { SortableListProvider } from 'hb-builder/contexts';
import { useBuilderContext } from 'hb-builder/hooks';

import DragAndDropListItem from './DragAndDropListItem';

const isFirstItem = (itemIndex) => itemIndex === 0;

const DragAndDropList = ({
  childrenComponent,
  componentName,
  flags,
  item,
  listButton,
  onChange,
  onToggleCallback,
  selectedChildParentItem,
}) => {
  const { setChildren } = useBuilderContext();

  const [forceClose, setForceClose] = useState(false);
  const [sortableIds, setSortableIds] = useState([]);

  const numberOfItems = item.children.length;

  const sortItems = (idsCollection) =>
    idsCollection
      .map((sortedId) => item.children.find((child) => child.id === sortedId))
      .filter(Boolean);

  useEffect(() => {
    setSortableIds(item.children.map((child) => child.id));
  }, [item.children, numberOfItems]);

  const handleDragStart = () => {
    setForceClose(true);
  };

  const handleDragEnd = ({ active, over }) => {
    const oldIndex = sortableIds.indexOf(active.id);
    const newIndex = sortableIds.indexOf(over.id);

    if (over.id === 'droppable' || newIndex < 0) return sortableIds;

    const reorderedIds = arrayMove(sortableIds, oldIndex, newIndex);

    setForceClose(false);
    saveNewItemOrder(reorderedIds);
  };

  const saveNewItemOrder = (reorderedIds) => {
    const reorderedItems = sortItems(reorderedIds);

    setChildren(item.id, reorderedItems);
  };

  return (
    <SortableListProvider listIds={sortableIds}>
      <ContentWrapper>
        <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <DroppableArea componentName={componentName}>
              {item.children.map((child, index) => (
                <DragAndDropListItem
                  childrenComponent={childrenComponent}
                  componentName={componentName}
                  currentItemNumber={numberOfItems}
                  flags={flags}
                  forceClose={forceClose}
                  isInitiallyOpen={isFirstItem(index)}
                  item={child}
                  itemPosition={index + 1}
                  key={child.id}
                  onChange={onChange}
                  onToggleCallback={onToggleCallback}
                  parentItem={selectedChildParentItem || item}
                  setForceClose={setForceClose}
                />
              ))}
            </DroppableArea>
            {listButton}
          </SortableContext>
        </DndContext>
      </ContentWrapper>
    </SortableListProvider>
  );
};

const DroppableArea = ({ children, componentName }) => {
  const { setNodeRef } = useDroppable({
    id: `droppable-${componentName}`,
  });

  return <Droppable ref={setNodeRef}>{children}</Droppable>;
};

export default DragAndDropList;

const ContentWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;

const Droppable = styled(Box).attrs(() => ({
  flexDirection: 'column',
}))``;
